import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react"; 
import axios from "axios";

import io from "socket.io-client";
import { StickerViewNode } from "../../components/ForumMode/ForumSticker/StickerViewNode";
import { QuestionnaireNodeCreator } from "../../components/ForumMode/core/QuestionnaireNodeCreator";
import { NodeTypeSelector } from "../../components/ForumMode/core/NodeTypeSelector";
import Notice from "../../components/Notice";
import url from "../../url.js";
import { genEdge, genNode_without_emoji } from "../../utils/ideaTool";
import { ModeContext } from "../../contexts/ModeContext";
import { newNode, newEdge } from '../../utils/ideaTool';
import { parseContentToHtml } from '../../utils/markdownParser';
import './ForumSticker.css';
import { StickerNodeCreator as CreateIdeaModal } from "../../components/ForumMode/ForumSticker/StickerNodeCreator";
import { buildStickerContext } from "../../components/ForumMode/ForumSticker/stickerContext";
import useUnreadMessages from "../../hooks/useUnreadMessages";
import { traceToRoot } from "../../components/ForumMode/core/CoreForumUtils";
import GroupToolPanel from "../../components/GroupToolPanel";
import { getNodeType } from "../../utils/nodeTypeUtils";
import { resolveViewNodeComponent } from "../../components/ForumMode/nodeViewRegistry";
import colors from '../../styles/colors';
import semanticColors from '../../styles/semanticColors';

import {
    ReactFlow,
    Handle,
    Position,
    MiniMap,
    Controls,
    ControlButton,
    Background,
    useNodesState,
    useEdgesState
  } from '@xyflow/react';
import '@xyflow/react/dist/style.css';


// Convert fetched nodes and edges data to initialNodes and initialEdges
const convertToInitialData = (safeNodes, safeEdges) => {

    
    
    
    // 建立 node map 與邊圖
    const nodeMap = new Map();
    const inDegree = new Map();
    const outDegree = new Map();
    const graph = new Map();
    const reverseGraph = new Map(); // 反向圖，用於找出父節點
  
    safeNodes.forEach((node) => {
      if (node && node.id) {
        nodeMap.set(node.id.toString(), node);
        inDegree.set(node.id.toString(), 0);
        outDegree.set(node.id.toString(), 0);
        graph.set(node.id.toString(), []);
        reverseGraph.set(node.id.toString(), []);
      }
    });
  
    safeEdges.forEach((edge) => {
      if (edge && edge.from && edge.to) {
        const from = edge.from.toString();
        const to = edge.to.toString();
        
        // Ensure the arrays exist before pushing
        if (graph.has(from)) {
          graph.get(from).push(to);
        }
        if (reverseGraph.has(to)) {
          reverseGraph.get(to).push(from);
        }
        
        if (inDegree.has(to)) {
          inDegree.set(to, inDegree.get(to) + 1);
        }
        
        // Track outgoing connections
        if (outDegree.has(from)) {
          outDegree.set(from, outDegree.get(from) + 1);
        }
      }
    });
  
    // 找出所有葉節點 (沒有出邊的節點)
    const leafNodes = [];
    for (const [id, outDeg] of outDegree.entries()) {
      if (outDeg === 0) {
        leafNodes.push(id);
      }
    }
    
    // 計算每個節點的層級
    const levels = new Map();
      const visited = new Set();
    
    // 從葉節點開始，向上計算層級
    const calculateLevels = (nodeId, level) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      levels.set(nodeId, level);
      
      // 遍歷父節點
      const parents = reverseGraph.get(nodeId) || [];
      for (const parent of parents) {
        calculateLevels(parent, level + 1);
        }
    };
    
    // 從每個葉節點開始計算層級
    leafNodes.forEach(leafId => {
      calculateLevels(leafId, 0);
    });
    
    // 處理沒有邊的節點（如引導單元），將它們放在最底層（level 0）
    for (const [id, node] of nodeMap.entries()) {
      if (!levels.has(id)) {
        levels.set(id, 0);
      }
    }
    
    // 按層級分組節點
    const levelGroups = new Map();
    for (const [id, level] of levels.entries()) {
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level).push(id);
    }
    
    // 計算節點位置
    const initialNodes = [];
    const nodePositions = new Map();
    
    // 垂直間距
    const verticalSpacing = 300;
    // 水平間距
    const horizontalSpacing = 250;
    
    // 從最底層開始，逐層向上放置節點
    const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => b - a); // 反轉排序，從大到小
    
    // 計算總高度，用於調整起始位置
    const totalHeight = sortedLevels.length * verticalSpacing;
    const startY = totalHeight / 2; // 從中間開始，向上和向下延伸
    
    sortedLevels.forEach(level => {
      const nodesInLevel = levelGroups.get(level);
      const y = startY - (level * verticalSpacing); // 從下往上排列
      
      // 計算這一層的總寬度
      const totalWidth = nodesInLevel.length * horizontalSpacing;
      const startX = -totalWidth / 2;
      
      // 放置這一層的節點
      nodesInLevel.forEach((nodeId, index) => {
        const x = startX + index * horizontalSpacing;
        nodePositions.set(nodeId, { x, y });
        
        const node = nodeMap.get(nodeId);
      if (node) {
        initialNodes.push({
            id: nodeId,
            position: { x, y },
          data: { 
            label: node.label ? node.label.split("\n")[0] : node.title, 
            source: node,
            isLeafNode: level === 0
          },
          type: 'clickNode',
          style: level === 0 ? {
            border: '3px solid #5C7CFA',
            borderRadius: '10px',
            boxShadow: '0 0 10px rgba(77, 171, 247, 0.5)',
            background: colors.info.background
            } : undefined
        });
      }
    });
    
    });
  
    const initialEdges = safeEdges.map((edge) => {
      if (edge && edge.from && edge.to) {
        return {
          id: `e${edge.from}-${edge.to}`,
          source: edge.from.toString(),
          target: edge.to.toString(),
          info: edge,
        };
      }
      return null;
    }).filter(edge => edge !== null);
  
    return { initialNodes, initialEdges };
};

const buildLayoutSnapshot = (nodesSnapshot, edgesSnapshot) => {
    const safeNodes = nodesSnapshot.map(node => {
        const source = node.data?.source || {};
        return {
            ...source,
            id: source.id ?? node.id,
            label: source.label ?? node.data?.label ?? source.title ?? '',
            title: source.title ?? source.label ?? node.data?.label ?? ''
        };
    });

    const safeEdges = edgesSnapshot.map(edge => {
        const raw = edge.info || {};
        return {
            from: raw.from ?? raw.source ?? edge.source,
            to: raw.to ?? raw.target ?? edge.target,
        };
    });

    return convertToInitialData(safeNodes, safeEdges);
};
  



export default function ForumModes_sticker({ showNavbar = true }) {
    // ✅ 初始化：將 localStorage 同步到 sessionStorage
    useEffect(() => {
        const syncStorage = () => {
            const activityId = localStorage.getItem('activityId');
            const groupId = localStorage.getItem('groupId');
            const userId = localStorage.getItem('userId');
            const joinCode = localStorage.getItem('joinCode');
            
            if (activityId) sessionStorage.setItem('activityId', activityId);
            if (groupId) sessionStorage.setItem('groupId', groupId);
            if (userId) sessionStorage.setItem('userId', userId);
            if (joinCode) sessionStorage.setItem('joinCode', joinCode);
            
            console.log('[ForumSticker] Storage 同步完成:', { activityId, groupId, userId, joinCode });
        };
        
        syncStorage();
    }, []);
    
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    const activityInfo = JSON.parse(localStorage.getItem('activityInfo'));
    const { isMapBoard } = useContext(ModeContext);
    const { updateLastReadTime, clearCache } = useUnreadMessages();
    const [open, setOpen] = useState(false);
    const [nodeContent, setNodeContent] = useState(null);
    const [ws, setWS] = useState(null);
    const [notices, setNotices] = useState([]); // Array to store notifications
    const activityId = sessionStorage.getItem("activityId");
    const networkRef = useRef(null);
    
    // Single source of truth for nodes and edges
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    
    // Performance monitoring
    const renderCountRef = useRef(0);
    useEffect(() => {
        renderCountRef.current += 1;
        console.log('[PERF] Component render #', renderCountRef.current);
        console.log('[PERF] Current state - nodes:', nodes.length, 'edges:', edges.length);
    });

    
    // State to track the currently dragged node
    const [draggedNodeId, setDraggedNodeId] = useState(null);
    
    const [groupId, setGroupId] = useState(sessionStorage.getItem('groupId'));
    const [createOpen, setCreateOpen] = useState(false);
    const [nodeTypeSelectorOpen, setNodeTypeSelectorOpen] = useState(false);
    const [questionnaireCreateOpen, setQuestionnaireCreateOpen] = useState(false);
    
    // 標記已讀：組件掛載時更新 lastRead，並定期更新（每 2 分鐘）
    useEffect(() => {
        const gid = sessionStorage.getItem('groupId');
        const aid = sessionStorage.getItem('activityId');
        if (!gid || !aid || !updateLastReadTime) return;

        const groupIdNum = parseInt(gid, 10);
        const activityIdNum = parseInt(aid, 10);

        // 立即更新一次
        updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
            console.error('ForumSticker 標記已讀失敗:', err)
        );

        // 每 2 分鐘定期更新
        const interval = setInterval(() => {
            updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
                console.error('ForumSticker 定期更新已讀失敗:', err)
            );
        }, 120000); // 2 分鐘

        return () => clearInterval(interval);
    }, [groupId, updateLastReadTime]);

    // 頁面可見性變化時更新（切換回標籤頁時）
    useEffect(() => {
        const gid = sessionStorage.getItem('groupId');
        const aid = sessionStorage.getItem('activityId');
        if (!gid || !aid || !updateLastReadTime) return;

        const groupIdNum = parseInt(gid, 10);
        const activityIdNum = parseInt(aid, 10);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
                    console.error('ForumSticker 可見性變化更新已讀失敗:', err)
                );
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [groupId, updateLastReadTime]);

    // 自訂 Node 元件
    const ClickableNode = React.memo(({ data, handleClickOpenByNodeId, edges: parentEdges, nodes: parentNodes }) => {
        // 建立 Date 物件，JavaScript 會自動解析為 UTC 時間
        const date = new Date(data.source.createdAt);

        // 將日期轉換為瀏覽器的當地時間字串
        const localTimeString = date.toLocaleString(); // 根據使用者瀏覽器的地區設定格式化

        // Check if this is a leaf node
        const isLeafNode = data.isLeafNode;
        
        console.log('[RENDER] ClickableNode rendered:', data.source.id);

        return (
        <div style={{
            borderRadius: 10,
            background: colors.info.background ,
            cursor: 'pointer',
            textAlign: 'center',
            minWidth: 100,
            padding: 'max(1vw, 10px)',
            maxWidth: 500,
            overflow: 'hidden',
            position: 'relative'
            }}>
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    handleClickOpenByNodeId(data.source);
                    sessionStorage.setItem('nodeId', data.source.id);
                }}
                onTouchEnd={(e) => {
                    // Prevent click on drag for mobile
                    if (e.defaultPrevented) return;
                }}
                style={{
                borderRadius: 10,
                background: colors.info.background ,
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 100,
                padding: 'max(1vw, 10px)',
                maxWidth: 200,
                overflow: 'hidden',
                touchAction: 'none',
                }}
            >
                {isLeafNode && (
                    <div style={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#5C7CFA',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        zIndex: 10
                    }}>
                        START
                    </div>
                )}
                <h3 style={{
                        padding: "4px 2px",
                        fontSize: "1rem", 
                        fontWeight: "bold", 
                        color: isLeafNode ? "#000" : semanticColors.grayText,
                        lineHeight: 1.5
                    }}>
                    {data.label}
                </h3>
                <div style={{
                    fontSize: "0.95rem", 
                    color: "#999", 
                }}>
                    By <strong>{data.source.author}</strong>    
                </div>
                <div style={{
                    fontSize: "0.95rem", 
                    color: "#999", 
                }}>
                    {localTimeString}
                </div>
                <Handle type="target" position={Position.Top} />
                <Handle type="source" position={Position.Bottom} />
            </div>
        </div>
        );
    }, (prevProps, nextProps) => {
        // Custom comparison function for React.memo
        // Only re-render if data.source or isLeafNode changes
        return prevProps.data.source.id === nextProps.data.source.id &&
               prevProps.data.isLeafNode === nextProps.data.isLeafNode &&
               prevProps.data.label === nextProps.data.label;
    });

    // Use refs to avoid nodeTypes recreation
    const edgesRef = useRef(edges);
    const nodesRef = useRef(nodes);
    
    useEffect(() => {
        edgesRef.current = edges;
        nodesRef.current = nodes;
    }, [edges, nodes]);

    const applyAutoLayout = useCallback(() => {
        const { initialNodes } = buildLayoutSnapshot(nodesRef.current, edgesRef.current);
        const layoutMap = new Map(initialNodes.map(node => [node.id, node.position]));
        setNodes(prev => prev.map(node => {
            const position = layoutMap.get(node.id);
            return position ? { ...node, position } : node;
        }));
    }, [setNodes]);
    
    const handleClickOpenByNodeId = useCallback((node) => {
        
        setNodeContent(null);
        setOpen(true);

        // 初始節點（包含問卷初始節點）帶有 isInitial 標記，直接用本地數據，不呼叫後端 API
        const isInitialNode = node?.isInitial === true;
        if (isInitialNode) {
            // For initial nodes, use local data (already has correct title and content)
            setNodeContent(node);
        } else {
            // For other nodes, fetch from backend
            fetchNode(node.id);
        }

        if (networkRef.current) {
            networkRef.current.focus(node.id, { scale: 1.5, offset: { x: 0, y: 0 } });
        }
    }, []);

    // 註冊自訂 node
    const nodeTypes = useMemo(() => {
        console.log('[PERF] nodeTypes useMemo recalculated');
        return {
            clickNode: (props) => (
                <ClickableNode 
                    {...props}
                    edges={edgesRef.current}
                    nodes={nodesRef.current}
                    handleClickOpenByNodeId={handleClickOpenByNodeId} 
                />
            ),
        };
    }, [handleClickOpenByNodeId]);

    const handleClose = () => {
        setOpen(false);
    };

    const fetchNode = async (nodeId) => {
        const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
        const activityId = sessionStorage.getItem('activityId');
        const groupId = sessionStorage.getItem('groupId');
        
        try {
            let response;
            if (isGuestMode) {
                // 访客模式：使用公开 API
                response = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/node/${nodeId}`);
            } else {
                // 认证模式：使用认证 API
                response = await axios.get(`${url.backendHost}api/nodes/${nodeId}`);
            }
            setNodeContent({ 
                ...response.data, 
                renderedContent: { __html: response.data.content } // 格式化後的 HTML 內容
            });
        } catch (err) {
            console.error(err);
        }
    };

    // 獲取 groupId，並在其後渲染 getNodes()
    const fetchGroupData = async () => {
        const jwtToken = localStorage.getItem('jwtToken');
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
        const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
        
        // ✅ 安全獲取參數
        const joinCode = sessionStorage.getItem('joinCode');
        const activityId = sessionStorage.getItem('activityId');
        const userId = sessionStorage.getItem('userId');
        
        // ✅ 參數驗證（訪客模式跳過 userId 檢查）
        if (!isGuestMode && (!activityId || activityId === 'null' || !userId || userId === 'null')) {
            console.error('[ForumSticker] 缺少必要參數:', { activityId, userId });
            alert('無法獲取活動資訊，請重新進入活動');
            window.location.href = '/main';
            return null;
        }
        
        // 訪客模式不需要獲取群組資料，直接返回
        if (isGuestMode) {
            console.log('[ForumSticker] 訪客模式，跳過獲取群組資料');
            return await getNodes();
        }
        
        const enterDifferentGroupEndpoint = `${url.backendHost}api/groups/teacher/${joinCode}/${userId}`;
        const getMyGroupEndpoint = `${url.backendHost}api/groups/${activityId}/${userId}`;
        
        console.log('[ForumSticker] 獲取群組資料:', { activityId, userId, joinCode });

        try {
            if (!joinCode) throw new Error('no-join-code');
            const response = await axios.get(enterDifferentGroupEndpoint, { headers, withCredentials: true });
            const fetchedGroupId = response.data.data[0].id;
            localStorage.setItem("groupId", fetchedGroupId);
            sessionStorage.setItem("groupId", fetchedGroupId);
            setGroupId(fetchedGroupId);
        } catch (error) {
            try {
                const response = await axios.get(getMyGroupEndpoint, { headers, withCredentials: true });
                const fetchedGroupId = response.data.data[0].id;
                localStorage.setItem("groupId", fetchedGroupId);
                sessionStorage.setItem("groupId", fetchedGroupId);
                setGroupId(fetchedGroupId);
            } catch (error) {
                console.error("獲取群組資料時出錯", error);
            }
        } finally {
            return await getNodes();
        }
    };

    // Initial fetch
    useEffect(() => {
        let isActive = true;
        (async () => {
            // connect to socket
            setWS(io.connect(url.socketioHost,{path: '/s/socket.io'}));

            // fetch group data
            await fetchGroupData().then(async (data) => {
                
                
                
                let allNodes = [...data.nodes];
                let allEdges = [...data.edges];
                
                // Add initial nodes if they exist
                try {
                    const { loadInitialNodesForSticker, validateInitialNodes } = require('../../utils/initialNodesLoader');
                    const groupId = sessionStorage.getItem('groupId');
                    const initialNodes = loadInitialNodesForSticker(activityInfo, groupId);
                    
                    if (initialNodes.length > 0) {
                        // 验证节点有效性
                        if (!validateInitialNodes(initialNodes)) {
                            console.error('[ForumSticker] Invalid initial nodes detected');
                        }
                        allNodes = [...allNodes, ...initialNodes];
                    }
                } catch (e) {
                    console.error('[ForumSticker] Error loading initial nodes:', e);
                }
                
                // refactor graph data for ReactFlow
                const { initialNodes, initialEdges } = convertToInitialData(allNodes, allEdges);
                
                

                // Ensure all nodes have a valid position
                const nodesWithPositions = initialNodes.map(node => {
                    // If node already has a position, keep it
                    if (node.position) {
                        return node;
                    }
                    
                    // Otherwise, assign a default position
                    return {
                        ...node,
                        position: { x: 100, y: 100 }
                    };
                });

                if (!isActive) return;
                // Set nodes and edges
                setNodes(nodesWithPositions);
                setEdges(initialEdges);
            });
        })();
        return () => { isActive = false; };
    }, []);
    
    // Listen to WebSocket events
    useEffect(() => {
        if (ws) {
            
            ws.on("connect", () => {
                
                addNotice("Connected to server successfully", "success");
            });

            ws.on(`node-recieve-${activityId}`, (body) => {
                if (body.groupId == sessionStorage.getItem('groupId')) {
                    
                    // addNotice(`New idea received from ${body.author}`, "info");
                    const newNode = {
                        createdAt: body.createdAt || new Date().toISOString(),
                        author: body.author,
                        ...genNode_without_emoji(body),
                    };
                    
                    
                    
                    // Convert the newNode to the format expected by ReactFlow
                    const reactFlowNode = {
                        id: newNode.id.toString(),
                        position: { x: 100, y: 100 }, // Default position
                        data: { 
                            label: newNode.label ? newNode.label.split("\n")[0] : newNode.title, 
                            source: newNode 
                        },
                        type: 'clickNode',
                    };
                    
                    // Update ReactFlow nodes
                    setNodes((prevNodes) => [...prevNodes, reactFlowNode]);
                    
                    // 收到新消息，清除該群組的未讀快取（其他群組會顯示紅點）
                    if (clearCache) {
                        clearCache(body.groupId);
                    }
                }
            });

            ws.on(`edge-recieve-${activityId}`, (body) => {
                if (body.groupId == sessionStorage.getItem("groupId")) {
                    
                    const newEdge = genEdge(body);
                    
                    // Convert to ReactFlow edge format
                    const reactFlowEdge = {
                        id: `e${newEdge.from}-${newEdge.to}`,
                        source: newEdge.from.toString(),
                        target: newEdge.to.toString(),
                        info: newEdge,
                    };
                    
                    // Update ReactFlow edges
                    setEdges((prevEdges) => [...prevEdges, reactFlowEdge]);

                    const my_node = nodes.find((node) => node.id == body.from);
                    

                    
                }
            });

            return () => {
                ws.off(`node-recieve-${activityId}`);
                ws.off("connect");
            };
        }
    }, [ws, nodes, edges, activityId, groupId]);

    const getNodes = async () => {
        // ✅ 安全獲取 groupId
        const groupId = sessionStorage.getItem("groupId");
        const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
        const activityId = sessionStorage.getItem('activityId');
        
        if (!groupId || groupId === 'null') {
            console.log('[ForumSticker] groupId 無效，嘗試獲取群組資料');
            if (!isGuestMode) {
                await fetchGroupData();
            }
            return;
        }

        try {
            const jwtToken = localStorage.getItem('jwtToken');
            
            // 訪客模式使用公開 API，否則使用認證 API
            let fetchData, fetchEdge;
            if (isGuestMode) {
                // 訪客模式：不發送 JWT token，使用公開 API
                fetchData = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/nodes`);
                fetchEdge = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/edges`);
            } else {
                // 認證模式：發送 JWT token
                const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
                fetchData = await axios.get(`${url.backendHost}api/nodes/all/${groupId}`, { headers, withCredentials: true });
                fetchEdge = await axios.get(`${url.backendHost}api/edges/all/${groupId}`, { headers, withCredentials: true });
            }
            
            // 處理節點數據：訪客模式和認證模式的數據結構不同
            const nodeData = (isGuestMode ? fetchData.data : fetchData.data[0].Nodes).map((node) => {
                const createdAt = node.createdAt ? new Date(node.createdAt) : null;
                const formattedCreatedAt = createdAt ? createdAt.toISOString() : 'Invalid Date';

                const generatedNode = {
                    ...genNode_without_emoji(node),
                    createdAt: formattedCreatedAt,
                    author: node.author,
                    authorId: node.authorId,
                };

                return generatedNode;
            });

            const edgeData = fetchEdge.data.map((edge) => genEdge(edge));

            
            
            
            if (!Array.isArray(nodeData)) { 
                addNotice("Invalid node data format", "error");
                throw new Error("Invalid node data format");
            }
            if (!Array.isArray(edgeData)) {
                addNotice("Invalid edge data format", "error");
                throw new Error("Invalid edge data format");
            }

            addNotice("Data loaded successfully", "success");
            return {
                nodes: nodeData,
                edges: edgeData,
            };
        } catch (error) {
            console.error('Error fetching data:', error);
            addNotice("Failed to load data. Please try again.", "error");
            if (error.response?.status === 401) {
                addNotice("Session expired. Please login again.", "warning");
                window.location.href = "/";
            }
            throw error;
        }
    };
    





    
              

    // Create a ref for the ReactFlow container
    const reactFlowContainerRef = useRef(null);
    // Store reactFlow instance for programmatic API calls (fitView, setCenter, etc.)
    const reactFlowInstanceRef = useRef(null);
    // Debounce timer ref for refresh
    const refreshDebounceRef = useRef(null);
    
    // Handle resize with a more robust approach
    useEffect(() => {
        if (!reactFlowContainerRef.current) return;
        
        // Create a debounced resize handler
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Force a re-render of the ReactFlow component
                setNodes(prevNodes => [...prevNodes]);
            }, 100);
        };
        
        // Create a ResizeObserver with error handling
        try {
            const resizeObserver = new ResizeObserver(entries => {
                // Use requestAnimationFrame to batch updates
                window.requestAnimationFrame(() => {
                    if (!Array.isArray(entries) || entries.length === 0) return;
                    handleResize();
                });
            });
            
            resizeObserver.observe(reactFlowContainerRef.current);
            
            // Clean up function
            return () => {
                clearTimeout(resizeTimeout);
                resizeObserver.disconnect();
            };
        } catch (error) {
            console.error("Error setting up ResizeObserver:", error);
            // Fallback to window resize event if ResizeObserver fails
            window.addEventListener('resize', handleResize);
            return () => {
                clearTimeout(resizeTimeout);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    // Handle node drag start - removed edge styling updates for better mobile performance
    const onNodeDragStart = useCallback((event, node) => {
        const startTime = performance.now();
        console.log('[DRAG] Start drag node:', node.id, 'at', startTime);
        console.log('[DRAG] Total nodes:', nodes.length, 'Total edges:', edges.length);
        setDraggedNodeId(node.id);
        const endTime = performance.now();
        console.log('[DRAG] onNodeDragStart took:', (endTime - startTime).toFixed(2), 'ms');
    }, [nodes.length, edges.length]);

    // Handle node drag
    const onNodeDrag = useCallback((event, node) => {
        const dragTime = performance.now();
        console.log('[DRAG] Dragging node:', node.id, 'position:', node.position);
    }, []);

    // Handle node drag end
    const onNodeDragEnd = useCallback(() => {
        const endTime = performance.now();
        console.log('[DRAG] End drag at', endTime);
        setDraggedNodeId(null);
    }, []);

    // Function to add a notice
    const addNotice = (message, type = 'info') => {
        const id = Date.now();
        setNotices(prev => [...prev, { id, message, type }]);
        // Auto remove after 5 seconds
        setTimeout(() => {
            setNotices(prev => prev.filter(notice => notice.id !== id));
        }, 5000);
    };

    // Function to remove a notice
    const removeNotice = (id) => {
        setNotices(prev => prev.filter(notice => notice.id !== id));
    };

    // Programmatic refresh function for the mind-map
    const refreshMindMap = useCallback((options = { preserveFocus: true, animate: true }) => {
        if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = setTimeout(() => {
            // Reapply layout before zooming/censing
            applyAutoLayout();

            // After nodes update, call fitView and optionally center on preserved node
            setTimeout(() => {
                const inst = reactFlowInstanceRef.current;
                if (!inst) return;
                try {
                    if (typeof inst.fitView === 'function') {
                        inst.fitView({ padding: 0.1 });
                    }
                } catch (e) {
                    console.warn('fitView not available on reactFlow instance', e);
                }

                const preserveId = sessionStorage.getItem('nodeId');
                if (options.preserveFocus && preserveId) {
                    const node = nodesRef.current.find(n => n.id == preserveId);
                    if (node) {
                        try {
                            if (typeof inst.setCenter === 'function') {
                                inst.setCenter(node.position.x, node.position.y, { duration: options.animate ? 300 : 0 });
                            } else if (typeof inst.setViewport === 'function') {
                                const zoom = inst.getZoom ? inst.getZoom() : undefined;
                                inst.setViewport({ x: node.position.x, y: node.position.y, zoom }, options.animate ? { duration: 300 } : undefined);
                            }
                        } catch (e) {
                            console.warn('Failed to center on node after refresh', e);
                        }
                    }
                }
            }, 50);
        }, 150);
    }, [setNodes]);

    // Expose programmatic refresh to window for other modules or tests
    useEffect(() => {
        try {
            window.refreshMindMap = refreshMindMap;
        } catch (e) {}
        return () => {
            try {
                if (window.refreshMindMap === refreshMindMap) delete window.refreshMindMap;
            } catch (e) {}
        };
    }, [refreshMindMap]);

    return (
        <div className="home-container" style={showNavbar ? undefined : { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0, background: '#e9e9ec' }}>            
            {/* Notice Container */}
            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                {notices.map(notice => (
                    <Notice
                        key={notice.id}
                        type={notice.type}
                        message={notice.message}
                        onClose={() => removeNotice(notice.id)}
                    />
                ))}
            </div>

            {
                isMapBoard && 
                <div 
                    ref={reactFlowContainerRef}
                    style={showNavbar ? { 
                        flex: "1 1 0%",
                        height: "95vh",
                        width: "95vw",
                        overflow: "auto",
                        position: "fixed",
                        top: "0px",
                        left: "0px",
                        marginLeft: "max(5vw, 64px)",
                        marginTop: "2vh",
                        boxSizing: 'border-box', 
                    } : {
                        position: 'absolute',
                        inset: 0,
                        overflow: 'hidden'
                    }}
                >
                    <ReactFlow
                        onInit={(inst) => { reactFlowInstanceRef.current = inst; }}
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={(changes) => {
                            console.log('[PERF] onNodesChange called with', changes.length, 'changes:', changes);
                            const startTime = performance.now();
                            onNodesChange(changes);
                            const endTime = performance.now();
                            console.log('[PERF] onNodesChange took:', (endTime - startTime).toFixed(2), 'ms');
                        }}
                        onEdgesChange={onEdgesChange}
                        onNodeDragStart={onNodeDragStart}
                        onNodeDrag={onNodeDrag}
                        onNodeDragStop={onNodeDragEnd}
                        proOptions={{ hideAttribution: true }}
                        nodeTypes={nodeTypes}
                        nodesDraggable={!isGuestMode}
                        nodesConnectable={!isGuestMode}
                        fitView
                        panOnScroll={true}
                        panOnDrag={[1, 2]}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        zoomOnDoubleClick={false}
                        preventScrolling={true}
                        selectNodesOnDrag={false}
                        defaultEdgeOptions={{
                          type: 'smoothstep',
                          animated: false,
                          style: { strokeWidth: 1, stroke: '#555' },
                          markerEnd: {
                            type: 'arrow',
                            color: '#000',
                            width: 60,
                            height: 60,
                          },
                        }}
                    >
                        <Controls>
                                {!isGuestMode && (
                                    <ControlButton onClick={() => setNodeTypeSelectorOpen(true)} title="新增節點" style={{ order: -1 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20 15a4 4 0 0 1-4 4H8l-4 3V7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="9" cy="11" r="1" fill="currentColor"/>
                                            <circle cx="12" cy="11" r="1" fill="currentColor"/>
                                            <circle cx="15" cy="11" r="1" fill="currentColor"/>
                                        </svg>
                                    </ControlButton>
                                )}
                                <ControlButton onClick={() => {
                                    // call refresh with preserveFocus and animation
                                    if (typeof window.refreshMindMap === 'function') {
                                        window.refreshMindMap({ preserveFocus: true, animate: true });
                                    }
                                }} title="重整心智圖" style={{ order: -1 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 12a9 9 0 11-3.2-6.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </ControlButton>
                            </Controls>
                        <MiniMap />
                        <Background variant="dots" gap={12} size={1} />
                    </ReactFlow>
                </div>
            }
            
            {
                isMapBoard === null && (
                    <div
                        style={{
                            marginLeft: "max(5vw, 64px)",
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                            width: '80vw',
                            boxSizing: 'border-box',
                            padding: `3vw`
                        }}
                    >
                        <p style={{ marginBottom: '5px' }}>活動資訊和說明</p>
                        {activityInfo && activityInfo.Activity && activityInfo.Activity.content ? (
                            <div 
                                dangerouslySetInnerHTML={{ __html: parseContentToHtml(activityInfo.Activity.content) }} 
                                style={{ padding: '20px' }}
                            />
                        ) : (
                            <div style={{ padding: '20px', color: '#334155', fontStyle: 'italic' }}>
                                目前沒有活動資訊
                            </div>
                        )}
                    </div>
                )
            }
            {(() => {
                const ViewNode = resolveViewNodeComponent(getNodeType(nodeContent), 'sticker');
                if (!ViewNode) return null;
                return (
                    <ViewNode
                        open={open}
                        onClose={handleClose}
                        nodeContent={nodeContent}
                        ws={ws}
                        groupId={groupId}
                        activityId={activityId}
                        graphNodes={nodes}
                        graphEdges={edges}
                        buildGroupContext={buildStickerContext}
                        mode="sticker"
                        onRefresh={getNodes}
                        onNodeUpdated={(nodeId) => {
                            if (nodeContent && nodeContent.id === nodeId) {
                                fetchNode(nodeId);
                            }
                        }}
                        onNodeDeleted={(nodeId) => {
                            if (nodeContent && nodeContent.id === nodeId) {
                                setNodeContent(null);
                                setOpen(false);
                            }
                        }}
                    />
                );
            })()}
            <NodeTypeSelector
                open={nodeTypeSelectorOpen}
                onClose={() => setNodeTypeSelectorOpen(false)}
                onSelect={(type) => {
                    if (type === 'questionnaire') {
                        setQuestionnaireCreateOpen(true);
                    } else {
                        setCreateOpen(true);
                    }
                }}
            />
            {createOpen && <CreateIdeaModal 
                open={createOpen} 
                onClose={() => setCreateOpen(false)} 
                ws={ws} 
                graphNodes={nodes}
                graphEdges={edges}
                nodePlan={{
                    title: "新增想法",
                    label: "想法標題",
                    tags: "idea",
                    helperMsg: "請為你的想法下一個標題，讓其他同學能更快速的你的想法！",
                    saffoldBody: [
                        "【💡我的想法】",
                        "【🧐我覺得更好的想法】",
                        "【❓我想知道】",
                        "【🙅🏻這個想法不能解釋】",
                        "【📄舉例和參考來源】",
                        "【✍🏻我的總結】"
                    ]
                }}
                buildGroupContext={buildStickerContext}
                mode="sticker"
            />}
            {questionnaireCreateOpen && (
                <QuestionnaireNodeCreator
                    open={questionnaireCreateOpen}
                    onClose={() => setQuestionnaireCreateOpen(false)}
                    ws={ws}
                    groupId={groupId}
                    activityId={activityId}
                />
            )}

            {/* 小組工具面板 */}
            {!isGuestMode && (
                <GroupToolPanel 
                    activityId={activityId}
                    groupId={sessionStorage.getItem('groupId')}
                    ws={ws}
                    onNodeAdded={(newNode) => {
                        // 重新載入節點數據
                        getNodes();
                    }}
                    mode="sticker"
                />
            )}

        </div>
    );
}